import React, { FC } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import { FormationCell } from '../types';
import '../components/BloodBowlGrid.css';

const COLUMNS = Array.from({ length: 15 }, (_, i) => String.fromCharCode(65 + i));
const ROWS = [-7, -6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6, 7];

interface OTTGridProps {
  layout: FormationCell[];
  opponentLayout: FormationCell[];
  highlights?: { row: number; col: number }[];
  onDrop: (id: number, row: number, col: number, isOpponent?: boolean) => void;
  onSquareClick?: (row: number, col: number) => void;
  getPlayerLabel: (id: number) => string;
  onCounterClick?: (id: number) => void;
}

// Helper component for the Draggable Counter on the Grid
// NOW ALSO A DROP TARGET!
const GridCounter: FC<{ 
  id: number; 
  label: string; 
  isOpponent: boolean; 
  onClick?: () => void;
  onDropOnMe: (droppedId: number, isOpponent: boolean) => void; // New Prop
}> = ({ id, label, isOpponent, onClick, onDropOnMe }) => {
  
  const [{ isDragging }, drag] = useDrag(() => ({
    type: isOpponent ? 'opponent-counter' : 'counter',
    item: { id, type: isOpponent ? 'opponent-counter' : 'counter' },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [id, isOpponent]);

  // Make the counter ITSELF a drop target
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ['counter', 'opponent-counter'],
    drop: (item: { id: number; type: string }, monitor) => {
      if (monitor.didDrop()) return; // Prevent bubbling if handled
      const droppedIsOpponent = item.type === 'opponent-counter';
      onDropOnMe(item.id, droppedIsOpponent);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }), [onDropOnMe]);

  // Combine refs
  const dragDropRef = (el: HTMLDivElement) => {
    drag(el);
    drop(el);
  };

  return (
    <div
      ref={dragDropRef}
      onClick={onClick}
      className={`counter ${isOpponent ? 'opponent' : ''}`}
      style={{
        backgroundColor: isOpponent ? '#d32f2f' : '#1976d2',
        color: 'white',
        borderRadius: '50%',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        fontWeight: 'bold',
        fontSize: '12px',
        border: isOver ? '2px solid yellow' : 'none' // Visual feedback
      }}
      title={`Player ${id}`}
    >
      {label}
    </div>
  );
};

const Cell: FC<{
  row: number;
  col: string;
  placement?: { id: number; isOpponent: boolean; label: string };
  isHighlighted: boolean;
  onDrop: (id: number, row: number, col: number, isOpponent?: boolean) => void;
  onClick?: () => void;
  onCounterClick?: (id: number) => void;
}> = ({ row, col, placement, isHighlighted, onDrop, onClick, onCounterClick }) => {
  const numericCol = COLUMNS.indexOf(col) + 1;

  const [{ isOver }, drop] = useDrop(() => ({
    accept: ['counter', 'opponent-counter'],
    drop: (item: { id: number; type: string }, monitor) => {
      if (monitor.didDrop()) return;
      const isOpponent = item.type === 'opponent-counter';
      onDrop(item.id, row, numericCol, isOpponent);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }), [row, numericCol, onDrop]); // <--- CRITICAL FIX: Add onDrop dependency

  const classes = ['cell'];
  if (col === 'D' || col === 'K') classes.push('wide-border');
  if (isHighlighted) classes.push('highlight-push');

  return (
    <div 
      ref={drop} 
      className={classes.join(' ')} 
      onClick={onClick}
      style={{ 
        position: 'relative', 
        borderBottom: row === -1 ? '3px solid black' : undefined,
        backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.5)' : undefined,
      }}
    >
      {placement && (
        <GridCounter 
          id={placement.id} 
          label={placement.label} 
          isOpponent={placement.isOpponent} 
          onClick={() => onCounterClick && onCounterClick(placement.id)}
          onDropOnMe={(droppedId, droppedIsOpponent) => {
             // Forward the drop to the cell's handler
             onDrop(droppedId, row, numericCol, droppedIsOpponent);
          }}
        />
      )}
      {isOver && !placement && <div className="cell-hover" />}
    </div>
  );
};

export const OTTGrid: FC<OTTGridProps> = ({ layout, opponentLayout, highlights = [], onDrop, onSquareClick, getPlayerLabel, onCounterClick }) => {
  
  // Helper to format row label
  const getRowLabel = (row: number) => {
    if (row < 0) return `D${Math.abs(row)}`;
    return `O${row}`;
  };

  return (
    <div className="board" style={{ gridTemplateRows: `repeat(${ROWS.length}, var(--cell-size))` }}>
      <div className="headers" />
      {COLUMNS.map(col => (
        <div key={col} className="col-header">{col}</div>
      ))}
      {ROWS.map(row => (
        <React.Fragment key={row}>
          {/* Update this line to use the new label helper */}
          <div className="row-header" style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
            {getRowLabel(row)}
          </div>
          
          {COLUMNS.map((col, ci) => {
            const numericCol = ci + 1;
            const player = layout.find(c => c.row === row && c.col === numericCol);
            const opponent = opponentLayout.find(c => c.row === row && c.col === numericCol);
            
            const placement = player 
              ? { id: player.playerNumber, isOpponent: false, label: getPlayerLabel(player.playerNumber) }
              : opponent 
                ? { id: opponent.playerNumber, isOpponent: true, label: 'OP' }
                : undefined;

            const isHighlighted = highlights.some(h => h.row === row && h.col === numericCol);

            return (
              <Cell
                key={`${row}-${col}`}
                row={row}
                col={col}
                placement={placement}
                isHighlighted={isHighlighted}
                onDrop={onDrop}
                onClick={() => isHighlighted && onSquareClick && onSquareClick(row, numericCol)}
                onCounterClick={onCounterClick}
              />
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
};